import {
  ApplyConfigurationStateMessage,
  BootstrappedMessage,
  DeviceType,
  DeviceTypeMessage,
  InboundMessage,
  LocationMessage,
  ScrollToElementMessage,
  SetAttributeValueMessage,
  UpdateLayoutConfig,
} from './inboundMessages';
import {
  configuratorReady,
  embedded,
  getHostDeviceType,
  getLocation,
  OutboundMessage,
  redirect,
} from './outboundMessages';
import { deserialize } from './serialized';

/**
 * Local copy of `getDeviceType` from `@canvas-logic/clp-viewer3d`
 * (`src/AdaptiveConfigurationDto.ts`). Do NOT replace this with an import from the
 * viewer3d barrel: EmbeddingService is imported by App.tsx and runs before routing, so a
 * value edge from here pins clp-viewer3d — and with it three.js (~815KB, ~40% of the entry
 * chunk) — into the eagerly loaded bundle, regardless of how the viewer is loaded elsewhere.
 * Kept byte-for-byte equivalent to upstream, including the absence of a
 * `window.matchMedia`-undefined guard.
 */
function getDeviceType(): 'desktop' | 'mobile' {
  const query = window.matchMedia('only screen and (max-width: 600px)');
  const isMobile = query && query.matches;
  return isMobile ? 'mobile' : 'desktop';
}

export type IFrameMessageType = (InboundMessage | OutboundMessage)['type'];

type IFrameMessageHandler = (inboundMessage: InboundMessage) => void;

type IFrameMessageEventListener<_T extends InboundMessage> = {
  once?: boolean;
  handler: IFrameMessageHandler;
};
/**
 * Communication wrapper between iframe host and the application.
 *
 * Message channel: window.postMessage
 * When application is used outside of iframe, no message is sent or handled.
 *
 * @see EmbeddingService.isEmbedded
 * @see window.postMessage
 */
export default class EmbeddingService {
  private ignoreEmbedCheck = false;
  private listenerMap = new Map<IFrameMessageType, IFrameMessageEventListener<InboundMessage>>();
  private readonly EMBEDDED_PING_INTERVAL = 100;
  private handler: number = 0;
  public initialLocation: Location | null = null;

  /**
   * Decide if we are working from inside the iframe
   */
  get isEmbedded() {
    if (this.ignoreEmbedCheck) {
      return true;
    }

    return window !== window.parent;
  }

  start(ignoreEmbedCheck = false) {
    this.ignoreEmbedCheck = ignoreEmbedCheck;

    if (this.isEmbedded) {
      window.addEventListener('message', this.handleMessage);
    }
  }

  private hasEmbeddedAlreadyChecked = false;

  /**
   * Awaiting initial message from the host page.
   * Presumably waiting for host to finish initialization.
   */
  async awaitBootstrapping() {
    if (!this.isEmbedded || this.hasEmbeddedAlreadyChecked) {
      return;
    }

    this.handler = window.setInterval(() => {
      this.sendMessage(embedded());
    }, this.EMBEDDED_PING_INTERVAL);

    const message: BootstrappedMessage = await this.awaitEventType('bootstrapped');
    this.initialLocation = message?.payload?.location ? deserialize(message.payload.location) : null;
    if (this.initialLocation && !message.payload.v) {
      // CP-1156: pre-v2 copies read window.location lazily, by which point a host anchor
      // handler may already have stripped the query — a shared configuration then opens as
      // the default one with nothing in the console to say why. Say it here.
      console.warn(
        'ConfiguratorAPI: the host page is running an outdated integration.js. Shared ' +
          'configuration links may open the default configuration. Re-copy the snippet from ' +
          'the site serving this iframe.',
      );
    }
    window.clearInterval(this.handler);
    this.handler = 0;
    this.hasEmbeddedAlreadyChecked = true;
  }

  sendReady() {
    this.sendMessage(configuratorReady());
  }

  redirect(url: string) {
    this.sendMessage(redirect(url));
  }

  /**
   * Obtain page location data.
   * Get current window data for standalone version and parent window data for embedded one.
   *
   * @see window.location
   */
  async getLocation(): Promise<Location> {
    if (!this.isEmbedded) {
      return window.location;
    }

    const message = await this.asyncCall<LocationMessage>(getLocation(), 'location');
    const location = deserialize(message.payload.location);
    return location;
  }

  async getHostDeviceType(): Promise<DeviceType> {
    if (!this.isEmbedded) {
      return getDeviceType();
    }

    const message = await this.asyncCall<DeviceTypeMessage>(getHostDeviceType(), 'device_type');
    return message.payload.deviceType;
  }

  /**
   * Send message to parent and wait for specific message in response.
   *
   * @param requestType app -> host request message type
   * @param responseType host -> app response message type
   * @private
   */
  private async asyncCall<T extends InboundMessage>(
    outboundMessage: OutboundMessage,
    responseType: InboundMessage['type'],
  ): Promise<T>;
  private async asyncCall(
    outboundMessage: OutboundMessage,
    responseType: InboundMessage['type'],
  ): Promise<InboundMessage> {
    this.sendMessage(outboundMessage);
    return this.awaitEventType(responseType);
  }

  sendMessage(outboundMessage: OutboundMessage) {
    if (!this.isEmbedded) {
      return;
    }

    window.parent.postMessage(outboundMessage, '*');
  }

  subscribeOnAttributeValueChange(callback: (inboundMessage: SetAttributeValueMessage) => void, _p0: never[]) {
    this.setMessageListener('set_attribute_value', (event) => {
      if (event.type === 'set_attribute_value') {
        callback(event);
      }
    });
    return () => {
      this.removeMessageListener('set_attribute_value');
    };
  }

  subscribeOnConfigurationStateChange(
    callback: (inboundMessage: ApplyConfigurationStateMessage) => void,
    _p0: never[],
  ) {
    this.setMessageListener('apply_configuration_state', (event) => {
      if (event.type === 'apply_configuration_state') {
        callback(event);
      }
    });
    return () => {
      this.removeMessageListener('apply_configuration_state');
    };
  }

  subscribeOnLayoutConfigChange(callback: (inboundMessage: UpdateLayoutConfig) => void, _p0: never[]) {
    this.setMessageListener('update_layout_config', (event) => {
      if (event.type === 'update_layout_config') {
        callback(event);
      }
    });
    return () => {
      this.removeMessageListener('update_layout_config');
    };
  }

  subscribeOnScrollToElement(callback: (msg: ScrollToElementMessage) => void, _p0: never[]) {
    this.setMessageListener('scroll_to_element', (event) => {
      if (event.type === 'scroll_to_element') {
        callback(event);
      }
    });
    return () => {
      this.removeMessageListener('scroll_to_element');
    };
  }

  private async awaitEventType<T extends InboundMessage>(type: T['type']): Promise<T>;
  private async awaitEventType(type: InboundMessage['type']): Promise<InboundMessage> {
    return new Promise((resolve) => {
      this.setMessageListener(
        type,
        (event) => {
          resolve(event);
        },
        true,
      );
    });
  }

  private setMessageListener(type: IFrameMessageType, handler: IFrameMessageHandler, once?: boolean) {
    if (this.listenerMap.has(type)) {
      console.warn(`Overriding iframe message listener (type: ${type})`);
    }

    this.listenerMap.set(type, {
      handler,
      once,
    });
  }

  private removeMessageListener(type: IFrameMessageType) {
    this.listenerMap.delete(type);
  }

  private handleMessage = (event: MessageEvent) => {
    const { data } = event;
    const inboundMessage: InboundMessage = data as InboundMessage;
    const type = inboundMessage.type;
    // Ignore
    if (!type) {
      return;
    }

    const listener = this.listenerMap.get(type);

    if (listener === undefined) {
      // console.warn(`Unhandled iframe message (type: ${type})`);
      return;
    }

    listener.handler(inboundMessage);

    if (listener.once) {
      this.removeMessageListener(type);
    }
  };

  stop() {
    if (this.handler > 0) {
      window.clearInterval(this.handler);
    }
    window.removeEventListener('message', this.handleMessage);
  }
}

export const embeddingService = new EmbeddingService();
